import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApproveRequestOutcome } from "@/components/RequestData/Provider/RequestUtilsProvider";
import { useRequestUtils } from "@/components/RequestData/Provider/useRequestUtils";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useRosterLookup } from "@/hooks/useRosterLookup";
import { useScheduleDayOf } from "@/hooks/useScheduleDayOf";
import { useFetchScheduleWeeks } from "@/hooks/useScheduleWeeks";
import type { NurseInShift, OpenShiftRequest, ReplaceShiftRequest, RequestResponsesStatus } from "@/utils/models";
import { acceptedHoursOf } from "@/utils/utilFunctions";
import { useShiftPredictionWarnings } from "@/utils/warningDialogs/useShiftPredictionWarnings";
import { isAgencyNurse } from "@/utils/warningDialogs/utils";
import { approveDeclineCount } from "../gapsRequests";
import { findShiftCreatedByApproval, shiftsOfNurse } from "../openShifts/gapsOpenShiftsModel";

type Params = {
	request: OpenShiftRequest | ReplaceShiftRequest;
	response: RequestResponsesStatus;
	/**
	 * The approval went through. `created` is the shift it made — for the assign-unit sheet — or `null` when it made
	 * none the phone can point to: a swap trades existing shifts, and the request day's weeks can fail to load.
	 */
	onApproved: (created: NurseInShift | null) => void;
	/**
	 * `true` from the tap until the approval ends WITHOUT applying anything (`false`). The sheet holds the request still
	 * in between: the approval itself closes the request or moves the person's answer on, and the step must not redraw
	 * as "gone" while it writes, nor while it animates out after handing on.
	 */
	onHoldChange: (isHeld: boolean) => void;
};

/**
 * The approve write behind the confirm sheet (P3-6): the desktop approval dialog's gates, then `approveRequest`.
 *
 * Gates: `useShiftPredictionWarnings.runChecks` on the hours the person accepted (`accepted_* ?? shift_*`) — agency,
 * overtime, daily hours and the staffing target of the request's unit, exactly the four `RequestApprovalDialog` raises.
 * It raises no PTO or employee-status warning, so neither is asked for here. Each is a `WarningDialog` that covers the
 * bottom bar (P3-13); confirming the last approves, cancelling any approves nothing.
 *
 * The write is `useRequestUtils().approveRequest` — the one every approval in the app funnels through — so open shifts,
 * replacements, swaps and the keep-or-cancel-agency choice all behave as they do on desktop, with its own snackbars and
 * analytics. It resolves what it did once its requests refresh is done, and that is what decides the outcome here —
 * never the provider's state, whose last update (the refreshed list) has not committed when the awaited call returns.
 *
 * The shift it created is told apart from the nurse's others in the request day's weeks (`useFetchScheduleWeeks`),
 * read before the write and again after it: flag off the old list, which the write edits in place; with
 * `staffing_schedule_cache` on the cached weeks, which the write marks stale, so the second read waits for their reload.
 *
 * One approval per opening: from the tap until it ends without applying, a second tap is ignored; once applied, the
 * confirm stays busy while the sheet hands on, so it cannot be pressed again during the exit.
 *
 * `gaps_approve_success` is recorded the moment the write reports Applied, inside the awaited call: the scrim and Escape
 * stay live while approving, and a sheet closed mid-write unmounts before any later render could record it. The hand-on
 * that follows — the created shift looked up, the assign-unit sheet or the day — is its own event,
 * `gaps_approve_handed_on`, and happens only while the sheet is still up.
 */
export const useGapsApprove = ({ request, response, onApproved, onHoldChange }: Params) => {
	const { track, trackError } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { getNurseById } = useRosterLookup(chosenDepartment.department_id);
	const fetchScheduleWeeks = useFetchScheduleWeeks();
	const scheduleDayOf = useScheduleDayOf();
	const { approveRequest } = useRequestUtils();
	const [isChecking, setIsChecking] = useState<boolean>(false);
	const [isWriting, setIsWriting] = useState<boolean>(false);
	const inFlightRef = useRef<boolean>(false);
	const nurseId = response.nurse_id;
	// The hours the approve write itself reads for this response, so the gates and the write run on the same ones.
	const hours = useMemo(() => acceptedHoursOf(request, response), [request, response]);
	const requestDay = useMemo(
		() => scheduleDayOf({ startTime: request.message.shift_start, shiftType: request.message.shift_type }).date,
		[request, scheduleDayOf],
	);
	// The nurse's shifts before the write, so the one it creates can be told apart from any they already had; `null`
	// when the request day's weeks failed to load, so nothing can be told apart.
	const shiftsBeforeRef = useRef<ReadonlySet<string> | null>(new Set());
	// An applied approval, waiting for the render that holds the provider's own local add (see the effect below).
	const [applied, setApplied] = useState<{ before: ReadonlySet<string> | null } | null>(null);
	const handledRef = useRef<{ before: ReadonlySet<string> | null } | null>(null);
	// The hand-on happens only while the sheet is still up: the weeks are read after the write, and it can close meanwhile.
	const isMountedRef = useRef<boolean>(true);
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const properties = useMemo(
		() => ({
			department_id: chosenDepartment.department_id,
			request_id: request.id,
			request_type: request.request_type,
			nurse_id: nurseId,
			decline_count: approveDeclineCount(request),
			is_partial: hours.start.toMillis() !== request.message.shift_start.toMillis() || hours.end.toMillis() !== request.message.shift_end.toMillis(),
		}),
		[chosenDepartment.department_id, request, nurseId, hours],
	);

	/** The approval ended without applying anything: the confirm can be pressed again and the sheet follows the data. */
	const release = useCallback((): void => {
		inFlightRef.current = false;
		setIsWriting(false);
		onHoldChange(false);
	}, [onHoldChange]);

	const performApprove = useCallback(async (): Promise<void> => {
		setIsWriting(true);
		const outcome = await approveRequest(request, nurseId);
		if (outcome === ApproveRequestOutcome.Dismissed) {
			// The keep-or-cancel-agency choice was dismissed: the user chose to approve nothing.
			track("gaps_approve_cancelled", { ...properties, step: "agency_choice" });
			release();
			return;
		}
		if (outcome === ApproveRequestOutcome.NotApplied) {
			// The provider has already said why (its error snackbar); this records where from.
			trackError("gaps_approve_error", { ...properties, outcome });
			release();
			return;
		}
		track("gaps_approve_success", properties);
		setApplied({ before: shiftsBeforeRef.current });
	}, [approveRequest, request, nurseId, properties, track, trackError, release]);

	// The shift the approval created, looked up from the render that commits `applied`. React renders an update together
	// with every update queued before it, so this render's old list already holds the provider's local add; read
	// straight after the awaited call, it would still be the render that started the write.
	useEffect(() => {
		if (applied === null || handledRef.current === applied) return;
		handledRef.current = applied;
		const { before } = applied;
		const handOn = (created: NurseInShift | undefined): void => {
			if (!isMountedRef.current) return;
			track("gaps_approve_handed_on", { ...properties, shift_id: created === undefined ? null : created.id });
			// No shift is the swap's (and the unloaded week's) case: the approval is done, with nothing here to place.
			onApproved(created === undefined ? null : created);
		};
		if (before === null) {
			handOn(undefined);
			return;
		}
		fetchScheduleWeeks(requestDay).then(
			(weeks) => handOn(findShiftCreatedByApproval({ before, after: shiftsOfNurse(weeks, nurseId), requestId: request.id, hours })),
			() => {
				trackError("gaps_approve_created_shift_lookup_error", properties);
				handOn(undefined);
			},
		);
	}, [applied, fetchScheduleWeeks, requestDay, nurseId, request.id, hours, properties, track, trackError, onApproved]);

	// The cascade calls back into whichever `performApprove` was current when it was CREATED; routing through a ref makes
	// it the latest one (the add sheet's fix for the same stale-callback trap).
	const performRef = useRef(performApprove);
	useEffect(() => {
		performRef.current = performApprove;
	}, [performApprove]);

	const warnings = useShiftPredictionWarnings(() => void performRef.current());

	const approve = useCallback(async (): Promise<void> => {
		if (inFlightRef.current) return;
		inFlightRef.current = true;
		onHoldChange(true);
		const nurse = getNurseById(nurseId);
		track("gaps_approve_start", properties);
		// Busy from the tap, so Cancel is off while the request day's weeks load: the approval goes on after them.
		setIsChecking(true);
		try {
			shiftsBeforeRef.current = new Set(shiftsOfNurse(await fetchScheduleWeeks(requestDay), nurseId).map((shift) => shift.id));
		} catch {
			// The approval doesn't need them; only the hand-on to the shift it creates does, and it then hands on nothing.
			trackError("gaps_approve_created_shift_lookup_error", properties);
			shiftsBeforeRef.current = null;
		}
		await warnings.runChecks({
			nurseId,
			startTime: hours.start,
			endTime: hours.end,
			isGlobalSalary: nurse?.isGlobalSalary,
			isAgency: nurse !== undefined && isAgencyNurse(nurse.employmentType),
			// The desktop approval raises no PTO warning (it passes `false`); matching it, not deciding a new gate here.
			hasPto: false,
			// The staffing target is the request's unit's; a request posted to no unit has none to check.
			locationName: request.location,
		});
		setIsChecking(false);
	}, [getNurseById, nurseId, fetchScheduleWeeks, requestDay, track, trackError, properties, warnings, hours, request.location, onHoldChange]);

	const cancelWarnings = useCallback((): void => {
		track("gaps_approve_cancelled", { ...properties, step: "warning" });
		warnings.handleCancelWarnings();
		release();
	}, [track, properties, warnings, release]);

	return { approve, cancelWarnings, warnings, hours, isBusy: isChecking || isWriting };
};
